import { inject, Injectable } from '@angular/core';
import { isDefined } from '@sama/js-core';
import { flatMap, union, values } from '@sama/micro-dash';
import { isEmpty, isUndefined } from 'lodash';
import { combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { TaskWorkspaceStore } from '../../../../../../core/h3ml/state/task-workspace-store';
import { RootFocusedAnnotationService } from '../../../../../../core/outputs/annotation/annotation-shared/utils/root-focused-annotation.service';
import { ObjectSortingService } from '../../../../../../core/shared/misc-services/object-sorting.service';
import { ObjectState } from '../../../../../../cross-team-shared/annotation/shared/state/object-state';
import {
  isShapeState,
  ShapeState,
} from '../../../../../../cross-team-shared/annotation/shared/state/shape-state';
import { keys } from '../../../../../../cross-team-shared/misc-utils/keys';
import { getOrThrow } from '../../../../../../cross-team-shared/misc-utils/strict-record';
import { debounceOnMicroQueue } from '../../../../../../shared/misc-utilities/misc-utils';

@Injectable()
export class AnnotationListService {
  private objectSortingService = inject(ObjectSortingService);
  private focusedAnnotationService = inject(RootFocusedAnnotationService);

  constructor(private taskWorkspaceStore: TaskWorkspaceStore) {}

  getShapes$() {
    return combineLatest([
      this.taskWorkspaceStore('scopedObjectId').$,
      this.taskWorkspaceStore('outputAnnotationInfo').$,
    ]).pipe(
      switchMap(([scopedObjectId]) => of(this.getShapes(scopedObjectId))),
    );
  }

  getObjects(scopedObjectId: number | undefined) {
    const { objectTransientInfo } = this.taskWorkspaceStore.state();

    const sortedObjects =
      this.objectSortingService.getSortedObjects(scopedObjectId);

    return sortedObjects.filter(
      (obj) => !getOrThrow(objectTransientInfo, obj.index).isFilteredOut,
    );
  }

  getScopedObjects$() {
    return combineLatest([
      this.taskWorkspaceStore('scopedObjectId').$,
      this.taskWorkspaceStore('objects').$,
      this.taskWorkspaceStore('objectTransientInfo').$,
    ]).pipe(
      // objects and transient info can be updated in the same action
      debounceOnMicroQueue(),
      map(([scopedObjectId]) => this.getObjects(scopedObjectId)),
    );
  }

  /**
   * Find the next annotation item in the current scope if only one annotation is selected at the moment.
   * Otherwise, it will return undefined.
   * Depending on the current selection, it will return the next shape or object.
   * */
  getNextAnnotationItem(direction: 'forward' | 'backward' = 'forward') {
    const { scopedObjectId } = this.taskWorkspaceStore.state();
    const focusedAnnotationStore =
      this.focusedAnnotationService.getAnnotationStore();

    if (focusedAnnotationStore) {
      const annotationsInScope = [
        ...this.getShapes(scopedObjectId),
        ...this.getObjects(scopedObjectId),
      ];

      const selectedAnnotation = focusedAnnotationStore.state();
      const indexFromList = annotationsInScope.findIndex(
        (annotation) =>
          annotation!.index === selectedAnnotation.index &&
          isShapeState(selectedAnnotation) === isShapeState(annotation!),
      );
      if (direction === 'forward') {
        return annotationsInScope[indexFromList + 1] ?? annotationsInScope[0];
      } else {
        return (
          annotationsInScope[indexFromList - 1] ??
          annotationsInScope[annotationsInScope.length - 1]
        );
      }
    }
    return undefined;
  }

  getFirstItemInObject(scopedObjectId: number | undefined):
    | {
        index: number;
        type: 'shape' | 'object';
      }
    | undefined {
    const firstShapeIndex = this.getShapes(scopedObjectId)[0]?.index;

    if (firstShapeIndex) {
      return { index: firstShapeIndex, type: 'shape' };
    }

    const firstAttachedObjectId = this.getObjects(scopedObjectId)[0]?.index;
    if (isDefined(firstAttachedObjectId)) {
      return { index: firstAttachedObjectId, type: 'object' };
    }
    return undefined;
  }

  getObjectsBetween(previousObject: ObjectState, nextObject: ObjectState) {
    const { objectSelection, scopedObjectId } = this.taskWorkspaceStore.state();
    if (isEmpty(objectSelection)) {
      return [nextObject.index];
    }
    const objectItems = this.getObjects(scopedObjectId);

    const minSortIndex = Math.min(
      nextObject.sortIndex,
      previousObject.sortIndex,
    );
    const maxSortIndex = Math.max(
      nextObject.sortIndex,
      previousObject.sortIndex,
    );

    const objectsToSelect = objectItems
      .filter(
        (object) =>
          object.sortIndex >= minSortIndex && object.sortIndex <= maxSortIndex,
      )
      .map((object) => object.index);

    return union(objectsToSelect, keys(objectSelection));
  }

  private getShapes(scopedObjectId: number | undefined) {
    if (isUndefined(scopedObjectId)) {
      return [];
    }

    const shapeInfoPerOutput =
      this.taskWorkspaceStore.state().outputAnnotationInfo;

    const shapes = flatMap(shapeInfoPerOutput, (shapeInfo) =>
      // @ts-ignore -- fix me
      values(shapeInfo.shapes).filter(
        // @ts-ignore -- fix me
        (shape) => shape.parentId === scopedObjectId,
      ),
    );
    // @ts-ignore -- fix me
    this.sortShapesByOutputAndSortIndex(shapes);
    return shapes;
  }

  private sortShapesByOutputAndSortIndex(shapes: ShapeState[]) {
    const displayOrder = this.taskWorkspaceStore.state().outputsByDisplayOrder;
    shapes.sort((a, b) => {
      const outputAIndex = displayOrder.findIndex(
        (out) => out.name === a.outputName,
      );
      const outputBIndex = displayOrder.findIndex(
        (out) => out.name === b.outputName,
      );
      if (outputAIndex === outputBIndex) {
        return b.index - a.index;
      }

      return outputAIndex - outputBIndex;
    });
  }
}
